// Analytics tab (§3): visibility curve per portal (Sistrix, 3a) + latest
// GSC 90-day snapshot KPI cards per portal (3b). Reads /seo-analytics.
// Chart is hand-drawn SVG — no external charting dependency.
import type { MarketingDeps } from "./marketing.js";
import { wireMotorRun } from "./marketing-poll.js";

interface VisSeries { profile: string; points: Array<{ value: number; capturedAt: string }> }
interface GscSnapshot { profile: string; capturedAt: string; metrics: Record<string, number> }
interface AnalyticsResponse { visibility: VisSeries[]; gsc: GscSnapshot[] }

const SVG = "http://www.w3.org/2000/svg";
const PALETTE = ["#0a84ff", "#ff9f0a", "#30d158", "#bf5af2", "#ff453a", "#64d2ff"];

// Sichtbarkeitskurven in den Portalfarben statt in Palettenreihenfolge (Uli 26.08.2026).
// Herkunft: SWAV und WHI aus der CRM-Tabelle vs3uk_wipcrm_akademien, WIP ist die Markenfarbe,
// FASZ die dominante Farbe der Seite.
// ⚠️ FASZ ist im Original #0e2248 — auf dem dunklen Hintergrund der Suite praktisch unsichtbar.
// Deshalb aufgehellt, aber im Blauton belassen und klar getrennt von SWAV-Cyan.
const PORTAL_FARBEN: Record<string, string> = {
  WIP: "#c04830",
  SWAV: "#27adde",
  WHI: "#dd7227",
  FASZ: "#4c7ad1",
};
// Unbekannte Profile fallen auf die alte Palette zurück — ein neues Portal bleibt so sichtbar,
// statt farblos zu werden.
const farbeVon = (profile: string, i: number): string =>
  PORTAL_FARBEN[profile.trim().toUpperCase()] ?? PALETTE[i % PALETTE.length]!;

const GSC_CARDS: Array<[string, string]> = [
  ["gsc_clicks_90d", "Klicks"],
  ["gsc_impressions_90d", "Impressions"],
  ["gsc_queries_90d", "Suchanfragen"],
  ["gsc_striking_distance", "Striking-Distance"],
];

function fmtNum(n: number): string {
  return n.toLocaleString("de-DE", { maximumFractionDigits: n < 100 ? 2 : 0 });
}

function el(tag: string, attrs: Record<string, string>): SVGElement {
  const e = document.createElementNS(SVG, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  return e;
}

function renderChart(host: HTMLElement, series: VisSeries[]): void {
  host.textContent = "";
  const drawn = series.filter((s) => s.points.length);
  if (!drawn.length) {
    host.innerHTML = '<p class="dim">Noch keine Sichtbarkeitsdaten. „Sichtbarkeit aktualisieren" startet den Sistrix-Pull.</p>';
    return;
  }
  const W = 760, H = 260, padL = 48, padR = 12, padT = 12, padB = 28;
  const all = drawn.flatMap((s) => s.points);
  const times = all.map((p) => Date.parse(p.capturedAt));
  const tMin = Math.min(...times), tMax = Math.max(...times);
  const vMax = Math.max(...all.map((p) => p.value)) * 1.08 || 1;
  const x = (t: number): number => padL + ((t - tMin) / (tMax - tMin || 1)) * (W - padL - padR);
  const y = (v: number): number => padT + (1 - v / vMax) * (H - padT - padB);

  const svg = el("svg", { viewBox: `0 0 ${W} ${H}`, class: "om-chart", role: "img" });
  // y gridlines + labels
  for (let i = 0; i <= 4; i++) {
    const v = (vMax / 4) * i;
    const yy = y(v);
    svg.appendChild(el("line", { x1: String(padL), y1: String(yy), x2: String(W - padR), y2: String(yy), class: "om-grid" }));
    const lbl = el("text", { x: String(padL - 6), y: String(yy + 3), class: "om-axis", "text-anchor": "end" });
    lbl.textContent = fmtNum(v);
    svg.appendChild(lbl);
  }
  // one polyline per portal
  drawn.forEach((s, i) => {
    const color = farbeVon(s.profile, i);
    const pts = s.points.map((p) => `${x(Date.parse(p.capturedAt))},${y(p.value)}`).join(" ");
    svg.appendChild(el("polyline", { points: pts, fill: "none", stroke: color, "stroke-width": "1.8" }));
    const last = s.points[s.points.length - 1]!;
    svg.appendChild(el("circle", { cx: String(x(Date.parse(last.capturedAt))), cy: String(y(last.value)), r: "2.6", fill: color }));
  });
  host.appendChild(svg);

  const legend = document.createElement("div");
  legend.className = "om-legend";
  drawn.forEach((s, i) => {
    const last = s.points[s.points.length - 1]!;
    const item = document.createElement("span");
    item.className = "om-legend-item";
    const dot = document.createElement("span");
    dot.className = "om-legend-dot";
    dot.style.background = farbeVon(s.profile, i);
    item.appendChild(dot);
    item.appendChild(document.createTextNode(`${s.profile} (${fmtNum(last.value)})`));
    legend.appendChild(item);
  });
  host.appendChild(legend);
}

function renderGsc(host: HTMLElement, snapshots: GscSnapshot[], formatDate: (s: string) => string): void {
  host.textContent = "";
  if (!snapshots.length) {
    host.innerHTML = '<p class="dim">Noch keine GSC-Daten. „GSC aktualisieren" startet den Pull.</p>';
    return;
  }
  for (const snap of snapshots) {
    const block = document.createElement("div");
    block.className = "om-portal";
    const h = document.createElement("h3");
    h.textContent = `${snap.profile} · Stand ${formatDate(snap.capturedAt)}`;
    block.appendChild(h);
    const cards = document.createElement("div");
    cards.className = "om-kpis";
    for (const [key, label] of GSC_CARDS) {
      const card = document.createElement("div");
      card.className = "om-kpi";
      const val = document.createElement("div");
      val.className = "om-kpi-val";
      val.textContent = key in snap.metrics ? fmtNum(snap.metrics[key]!) : "—";
      const lab = document.createElement("div");
      lab.className = "om-kpi-label";
      lab.textContent = label;
      card.appendChild(val);
      card.appendChild(lab);
      cards.appendChild(card);
    }
    block.appendChild(cards);
    host.appendChild(block);
  }
}

export function initAnalyticsTab(m: HTMLElement, deps: MarketingDeps): void {
  const chartHost = m.querySelector("[data-vis-chart]") as HTMLElement | null;
  const gscHost = m.querySelector("[data-gsc-cards]") as HTMLElement | null;
  const msg = m.querySelector('[data-msg="analytics"]') as HTMLElement | null;
  if (!chartHost || !gscHost) return;

  const load = async (): Promise<void> => {
    const r = await deps.api<AnalyticsResponse>("/api/marketing/seo-analytics");
    renderChart(chartHost, r.data?.visibility ?? []);
    renderGsc(gscHost, r.data?.gsc ?? [], deps.formatDate);
  };

  wireMotorRun({
    deps, modal: m,
    btn: m.querySelector('[data-action="run-sistrix"]'),
    msg, jobType: "sistrix_vis",
    runUrl: "/api/marketing/motors/sistrix_vis/run",
    onDone: load,
  });
  wireMotorRun({
    deps, modal: m,
    btn: m.querySelector('[data-action="run-gsc"]'),
    msg, jobType: "gsc_pull",
    runUrl: "/api/marketing/motors/gsc_pull/run",
    onDone: load,
  });

  void load();
}
